import { TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { formStyle } from '../../../contractStyles';

const MIN_ROWS = 2;
const MAX_ROWS = 5;

const Textarea = forwardRef(({ onChange, value, ...props }, ref) => {
  const textareaRef = useRef(null);
  const textareaProps = { ...props };
  delete textareaProps.ownerState;

  useImperativeHandle(ref, () => textareaRef.current);

  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';

    const styles = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.2;
    const verticalPadding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    const verticalBorder = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
    const extraHeight = verticalPadding + verticalBorder;
    const minHeight = lineHeight * MIN_ROWS + extraHeight;
    const maxHeight = lineHeight * MAX_ROWS + extraHeight;
    const contentHeight = textarea.scrollHeight + verticalBorder;

    textarea.style.height = `${Math.max(minHeight, Math.min(contentHeight, maxHeight))}px`;
    textarea.style.overflowY = 'auto';
  }, []);

  useLayoutEffect(resizeTextarea, [resizeTextarea, value]);

  useEffect(() => {
    let animationFrameId;
    const handleResize = () => {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(resizeTextarea);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
    };
  }, [resizeTextarea]);

  return <textarea {...textareaProps} ref={textareaRef} value={value ?? ''} onChange={onChange} />;
});

Textarea.propTypes = {
  onChange: PropTypes.func,
  ownerState: PropTypes.object,
  value: PropTypes.string,
};

MultilineTextInput.propTypes = {
  fieldName: PropTypes.string,
    setFocusedField: PropTypes.func,
    disabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default function MultilineTextInput({ fieldName, setFocusedField,  disabled, placeholder = '' }) {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ name: fieldName, control }); 

  return (
    <TextField
      inputRef={field.ref}
      key={field.id}
      onFocus={() => setFocusedField?.(fieldName)}
      inputProps={{
        onBlur: () => setFocusedField?.(null),
      }}
      placeholder={placeholder}
      autoComplete="off"
      fullWidth
      size="small"
      disabled={disabled}
      value={field.value ?? ''}
      error={!!fieldState.error}
      onChange={(event) => field.onChange(event.target.value)}
      helperText={fieldState.error?.message}
      sx={formStyle}
      InputProps={{
        inputComponent: Textarea,
        sx: { background: 'white' },
      }}
    />
  );
}
